/**
 * IndexedDB-based session storage for the rAI Chat full page.
 *
 * DB: "rai_chat", version 1
 * Stores:
 *   - sessions: keyed by GAIA session_id
 *   - messages: auto-increment PK, indexed by session_id
 */

const DB_NAME = 'rai_chat';
const DB_VERSION = 1;
const SESSIONS_STORE = 'sessions';
const MESSAGES_STORE = 'messages';

let dbPromise = null;

/**
 * Open (or create) the IndexedDB database. Cached after first call.
 * @returns {Promise<IDBDatabase>}
 */
export function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
        db.createObjectStore(SESSIONS_STORE, { keyPath: 'session_id' });
      }
      if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
        const msgStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id', autoIncrement: true });
        msgStore.createIndex('by_session', 'session_id', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

// ---------------------------------------------------------------------------
// Generic helpers
// ---------------------------------------------------------------------------

function tx(storeName, mode = 'readonly') {
  return openDB().then(db => db.transaction(storeName, mode).objectStore(storeName));
}

function req(idbReq) {
  return new Promise((resolve, reject) => {
    idbReq.onsuccess = () => resolve(idbReq.result);
    idbReq.onerror = () => reject(idbReq.error);
  });
}

// ---------------------------------------------------------------------------
// Sessions CRUD
// ---------------------------------------------------------------------------

/**
 * Create a new chat session record.
 * @param {string} sessionId  The GAIA session ID (used as PK)
 * @param {string} [title]    Optional initial title
 * @returns {Promise<object>}  The stored session object
 */
export async function createChatSession(sessionId, title = '') {
  const now = new Date().toISOString();
  const session = {
    session_id: sessionId,
    title,
    created_at: now,
    last_activity: now,
    gaia_chat_history: [],
  };
  const store = await tx(SESSIONS_STORE, 'readwrite');
  await req(store.put(session));
  return session;
}

/**
 * Update session's last_activity timestamp and optionally title / chatHistory.
 * @param {string} sessionId
 * @param {object} [fields]  Keys to merge: title, gaia_chat_history
 */
export async function updateSession(sessionId, fields = {}) {
  const store = await tx(SESSIONS_STORE, 'readwrite');
  const existing = await req(store.get(sessionId));
  if (!existing) return;
  Object.assign(existing, fields, { last_activity: new Date().toISOString() });
  await req(store.put(existing));
}

/**
 * Get a single session by ID.
 */
export async function getSession(sessionId) {
  const store = await tx(SESSIONS_STORE);
  return req(store.get(sessionId));
}

/**
 * Get all sessions, sorted newest-first by last_activity.
 * @returns {Promise<object[]>}
 */
export async function getAllSessions() {
  const store = await tx(SESSIONS_STORE);
  const all = await req(store.getAll());
  return all.sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity));
}

/**
 * Delete a session and its messages.
 */
export async function deleteSessionById(sessionId) {
  const db = await openDB();
  const txn = db.transaction([SESSIONS_STORE, MESSAGES_STORE], 'readwrite');
  txn.objectStore(SESSIONS_STORE).delete(sessionId);

  // Delete all messages belonging to this session via cursor on the index
  const msgStore = txn.objectStore(MESSAGES_STORE);
  const idx = msgStore.index('by_session');
  const cursorReq = idx.openCursor(IDBKeyRange.only(sessionId));
  await new Promise((resolve, reject) => {
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

// ---------------------------------------------------------------------------
// Messages CRUD
// ---------------------------------------------------------------------------

/**
 * Add a message to a session.
 * @param {string} sessionId
 * @param {string} role  'user' | 'ai' | 'error'
 * @param {string} text
 * @returns {Promise<number>}  The auto-generated message id
 */
export async function addMessage(sessionId, role, text) {
  const store = await tx(MESSAGES_STORE, 'readwrite');
  return req(
    store.add({
      session_id: sessionId,
      role,
      text,
      time: new Date().toISOString(),
    }),
  );
}

/**
 * Get all messages for a session, ordered by insertion (auto-increment PK).
 * @param {string} sessionId
 * @returns {Promise<object[]>}
 */
export async function getSessionMessages(sessionId) {
  const store = await tx(MESSAGES_STORE);
  const idx = store.index('by_session');
  return req(idx.getAll(IDBKeyRange.only(sessionId)));
}

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

/**
 * Delete sessions (and their messages) older than `days` days.
 * @param {number} [days=14]
 */
export async function clearOldSessions(days = 14) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const all = await getAllSessions();
  const stale = all.filter(s => new Date(s.last_activity) < cutoff);
  for (const s of stale) {
    await deleteSessionById(s.session_id);
  }
}
